import type { TargetProbeLegRecord, TargetProbeTripReason } from '@nadoutong/contracts';
import type { I18nKey, TParams } from '../../i18n/index.ts';
import { formatLocalTimestamp } from '../../components/formatters.ts';
import { disabledDetail } from '../tenant-routing/disabledNote.ts';
import { qualityEvidence } from './qualityEvidence.ts';
import { brokenFaultMode, guardFault, silentFaultMode } from './legFaults.ts';
import {
  immunityMinutesRemaining,
  legAgoText,
  tripReasonKey,
  type LegLamp,
  type LegSkuArming,
} from './legModel.ts';

type Translate = (key: I18nKey, params?: TParams) => string;

export function isGuardTriggerReason(value: string): value is GuardTriggerReason {
  return value === 'overload' || value === 'first_content_stream'
    || value === 'first_content_nonstream' || value === 'prelude_fail';
}

function join(parts: readonly string[]): string {
  return parts.filter((part) => part.length > 0).join(' · ');
}

export type GuardTriggerReason = 'overload' | 'first_content_stream' | 'first_content_nonstream' | 'prelude_fail';

export function triggerKey(reason: TargetProbeTripReason | 'broken' | GuardTriggerReason | null): I18nKey {
  switch (reason) {
    case 'overload':
      return 'guaranteed.leg.trigger.overload';
    case 'first_content_stream':
      return 'guaranteed.leg.trigger.first_content_stream';
    case 'first_content_nonstream':
      return 'guaranteed.leg.trigger.first_content_nonstream';
    case 'prelude_fail':
      return 'guaranteed.leg.trigger.prelude_fail';
    case 'silent':
      return 'guaranteed.leg.trigger.silent';
    case 'broken':
      return 'guaranteed.leg.trigger.broken';
    case 'chain':
      return 'guaranteed.leg.trigger.chain';
    case 'jev':
      return 'guaranteed.leg.trigger.jev';
    case 'quality':
      return 'guaranteed.leg.trigger.quality';
    case 'probe':
    case null:
      return 'guaranteed.leg.trigger.probe';
  }
}

export interface LegEvidenceOptions {
  /**
   * Drop words a caller's own headline already says: the legacy 链内证据
   * alias next to 链内熔断, and the shadow 未真正摘除 tail.
   */
  readonly compact?: boolean;
  /** Print 「→ 时间 摘除」 on a tripped leg; off for a removal record. Default true. */
  readonly trippedAt?: boolean;
}

/** Names the trigger (probe / quality / chain / Jev), shadow vs act, evidence, and relative time. */
export function legEvidence(
  leg: TargetProbeLegRecord,
  lamp: LegLamp,
  nowMs: number,
  t: Translate,
  arming?: LegSkuArming,
  options: LegEvidenceOptions = {},
): string {
  const ago = legAgoText(leg.last_probe_at, nowMs, t);
  const http = leg.last_status_code === null ? '' : `HTTP ${String(leg.last_status_code)}`;
  const rawExcerpt = leg.last_error_excerpt ?? '';
  const quality = qualityEvidence(rawExcerpt, t);
  // `http_403` next to `HTTP 403` says the same thing twice; alone it reads as `HTTP 403`.
  const excerpt = quality ?? (/^http_\d{3}$/.test(rawExcerpt)
    ? (http.length > 0 ? '' : `HTTP ${rawExcerpt.slice(5)}`)
    : rawExcerpt);
  if (lamp === 'manual') return manualLegEvidence(leg, nowMs, t);
  // A gateway predating #2619 omits the round counters; say nothing rather than `undefined`.
  const failedRounds: unknown = leg.consecutive_failed_rounds;
  const failedKnown = typeof failedRounds === 'number' && Number.isFinite(failedRounds);
  const failed = failedKnown ? String(failedRounds) : '';
  const threshold = arming?.tripFailedRounds ?? null;
  if (lamp === 'failing') {
    // The round counters only move on scheduled rounds. With the SKU's probe
    // mode off (a failure seen through 立即探测 / 试打) or a zero streak next
    // to a failed latest status, 「连续 0/1 轮」 says nothing true: omit it.
    const informative = arming?.probesOn === true && failedKnown && leg.consecutive_failed_rounds > 0;
    const rounds = threshold === null || !informative
      ? ''
      : t('guaranteed.leg.rounds_ratio', { failed, threshold: String(threshold) });
    return join([t('guaranteed.leg.verdict.failing'), http, excerpt, ago, rounds]);
  }
  if (lamp === 'unprobed') return t('guaranteed.leg.unprobed');
  if (lamp === 'immune') {
    const left = immunityMinutesRemaining(leg.immune_until, nowMs) ?? 0;
    return t('guaranteed.leg.verdict.immune', {
      ago: ago.length > 0 ? ago : t('guaranteed.leg.never'),
      minutes: String(left),
    });
  }
  if (lamp === 'ok') return t('guaranteed.leg.verdict.serving', { detail: join([ago, http]) });
  const silent = silentFaultMode(leg);
  const broken = brokenFaultMode(leg);
  const guard = guardFault(leg);
  const reason = silent !== null
    ? 'silent'
    : broken !== null
      ? 'broken'
      : guard !== null && isGuardTriggerReason(guard.faultKind)
        ? guard.faultKind
        : lamp === 'shadow' ? leg.shadow_trip_reason : leg.trip_reason;
  // 「质量不达标：近 120 分钟 23% 探针失败/超慢（5/22 发）」
  const trigger = reason === 'quality' && quality !== null
    ? t('guaranteed.leg.trigger.with_evidence', { trigger: t(triggerKey(reason)), evidence: quality })
    : t(triggerKey(reason));
  const named = lamp === 'shadow' ? t('guaranteed.leg.trigger.shadow', { trigger }) : trigger;
  const compact = options.compact === true;
  const legacy = reason === 'chain' && !compact ? t(tripReasonKey('chain')) : '';
  const tail = lamp === 'shadow' && !compact ? t('guaranteed.leg.verdict.not_removed') : '';
  const streak = lamp === 'tripped' && reason === 'probe' && failedKnown && leg.consecutive_failed_rounds > 0
    ? t('guaranteed.leg.rounds_streak', { failed })
    : '';
  const at = lamp === 'tripped' && options.trippedAt !== false ? trippedAtText(leg.tripped_at ?? null, t) : '';
  const evidence = reason === 'quality' && quality !== null ? '' : excerpt;
  // Red-team P2-3: with probes not in act nothing releases a Jev trip early.
  const expiryOnly = lamp === 'tripped' && reason === 'jev' && arming?.probeAct === false && !compact
    ? t('guaranteed.leg.jev_expiry_only')
    : '';
  return join([named, legacy, evidence, http, ago, streak, at, tail, expiryOnly]);
}

/** 「→ 09-24 14:02 摘除」 in local time; empty when the gateway did not send it. */
export function trippedAtText(iso: string | null, t: Translate): string {
  if (iso === null) return '';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  return t('guaranteed.legs.tripped_at', { at: formatLocalTimestamp(iso).slice(5) });
}

type LegMember = TargetProbeLegRecord['members'][number];

/** The disabled member whose note speaks for the leg: the most recently disabled. */
export function latestDisabledMember(leg: TargetProbeLegRecord): LegMember | null {
  let latest: LegMember | null = null;
  for (const member of leg.members) {
    if (member.enabled) continue;
    if (latest === null) {
      latest = member;
      continue;
    }
    const at = Date.parse(member.disabled_at ?? '');
    const best = Date.parse(latest.disabled_at ?? '');
    if (Number.isFinite(at) && (!Number.isFinite(best) || at > best)) latest = member;
  }
  return latest;
}

/**
 * 「{备注 | 无备注} · {谁} · {多久前}」, then 「最近探测 HTTP {code} · {多久前}」
 * once a probe (立即探测) ran after the removal, so the operator can see
 * whether the upstream is back before 加回.
 */
export function manualLegEvidence(leg: TargetProbeLegRecord, nowMs: number, t: Translate): string {
  const member = latestDisabledMember(leg);
  const detail = disabledDetail(member ?? {}, nowMs, t);
  const disabledAt = Date.parse(member?.disabled_at ?? '');
  const probedAt = Date.parse(leg.last_probe_at ?? '');
  const probedSince = Number.isFinite(probedAt)
    && leg.last_status_code !== null
    && (!Number.isFinite(disabledAt) || probedAt >= disabledAt);
  const probe = probedSince
    ? t('guaranteed.leg.manual_probe', {
      code: String(leg.last_status_code),
      ago: legAgoText(leg.last_probe_at, nowMs, t),
    })
    : '';
  return join([detail, probe]);
}

/** Notes left on the disabled members of a partly disabled leg (hover text). */
export function disabledMemberNotes(leg: TargetProbeLegRecord, t: Translate): string {
  const notes = leg.members
    .filter((member) => !member.enabled)
    .map((member) => member.disabled_note ?? '')
    .filter((note) => note.length > 0);
  return notes.length === 0 ? '' : t('guaranteed.leg.partly_disabled_notes', { notes: [...new Set(notes)].join('；') });
}
